import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { GameCard } from './GameCard';
import { TournamentCard } from './TournamentCard';
import { Trophy, Calendar, Plus, Upload, FileUp, Lock } from 'lucide-react';
import { cardShadow } from '../../lib/designTokens';

export const GamesTab = ({
  games,
  tournaments,
  team,
  completedGames,
  isHeadCoach,
  effectiveIsElite,
  importMenuOpen,
  addMenuOpen,
  setImportMenuOpen,
  setAddMenuOpen,
  nextGameDividerRef,
  deleteGame,
  openGameImport,
  openGameData,
  openGameInsights,
  gameHasInsights,
  setEliteModal,
  setIsImportingCalendar,
  setIsAddingGame,
  setIsCreatingTournament,
  setActiveLineupGame,
  setActiveGame,
  setActivePlanGame,
  setEditingGame,
  setEditGamePanelGame,
  setActiveTournament,
  setEnterResultsGame,
  hasArchivedSeasons,
  onOpenSeasonHistory,
}: {
  // Already scoped to the team's current season by App.tsx. Prior-season games
  // live in Season History, reachable via onOpenSeasonHistory below.
  games: any[];
  tournaments: any[];
  team: any;
  completedGames: any[];
  isHeadCoach: boolean;
  effectiveIsElite: boolean;
  importMenuOpen: boolean;
  addMenuOpen: boolean;
  setImportMenuOpen: any;
  setAddMenuOpen: any;
  nextGameDividerRef: any;
  deleteGame: (gameId: string) => void;
  /** The one GameChanger import; null = not tied to a game (files say which). */
  openGameImport: (game: any | null) => void;
  /** A game card's data button: box score when it has stats, else the import. */
  openGameData: (game: any) => void;
  /** A game card's saved AI insights (read-only, no Claude call). */
  openGameInsights: (game: any) => void;
  gameHasInsights: (game: any) => boolean;
  setEliteModal: any;
  setIsImportingCalendar: any;
  setIsAddingGame: any;
  setIsCreatingTournament: any;
  setActiveLineupGame: any;
  setActiveGame: any;
  setActivePlanGame: any;
  setEditingGame: any;
  setEditGamePanelGame: any;
  setActiveTournament: any;
  setEnterResultsGame: any;
  hasArchivedSeasons?: boolean;
  onOpenSeasonHistory?: () => void;
}) => {
  const showPastSeasons = isHeadCoach && hasArchivedSeasons && !!onOpenSeasonHistory;

  // Tournaments carry no season tags (the rollover only backfills games), so scope
  // them by derivation: keep one when it has a game in this season's list, or when
  // its start date falls in the season year. An undated or unparseable tournament is
  // kept, so a bad date shows up rather than silently vanishing.
  const seasonTournaments = (() => {
    const year = Number(team?.seasonYear);
    if (!Number.isFinite(year) || year <= 0) return tournaments;
    const linkedIds = new Set(games.map((g: any) => g.tournamentId).filter(Boolean));
    return tournaments.filter((t: any) => {
      if (linkedIds.has(t.id)) return true;
      const started = new Date(t.startDate);
      if (!t.startDate || isNaN(started.getTime())) return true;
      return started.getFullYear() === year;
    });
  })();

  // Matches the Practice Mode card below it: bordered panel, title, one line of
  // context, full-width outlined button.
  const PastSeasonsCard = () => (
    <div style={{ backgroundColor: '#FFFFFF', boxShadow: cardShadow, borderRadius: '16px', padding: '16px' }}>
      <p style={{ fontSize: '13px', fontWeight: 600, color: '#141A21' }}>Earlier Seasons</p>
      <p style={{ fontSize: '12px', fontWeight: 400, color: '#5A6672', marginTop: '2px' }}>Every past season is saved. Games, stats, and leaders.</p>
      <button
        onClick={onOpenSeasonHistory}
        style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '8px', backgroundColor: 'transparent', color: '#141A21', fontSize: '12px', fontWeight: 600, border: '1px solid #141A21', cursor: 'pointer' }}
      >
        View Past Seasons →
      </button>
    </div>
  );

  return (
    <>
              {/* Click-outside backdrop for the header popover menus */}
              {(importMenuOpen || addMenuOpen) && (
                <div
                  onClick={() => { setImportMenuOpen(false); setAddMenuOpen(false); }}
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                />
              )}
              {/* Section header. It lifts above the backdrop only while one of its
                  menus is open; a permanent 45 would outrank the app header (z-20)
                  and poke these icons through the account menu. */}
              <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: (importMenuOpen || addMenuOpen) ? 45 : undefined }}>
                <h2 className="text-2xl font-black italic uppercase tracking-tight">Schedule</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                  {/* Import menu */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => { setImportMenuOpen(o => !o); setAddMenuOpen(false); }}
                      title="Import"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5A6672' }}
                    >
                      <FileUp style={{ width: '20px', height: '20px' }} />
                    </button>
                    {importMenuOpen && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '6px', zIndex: 50, minWidth: '220px', backgroundColor: '#FFFFFF', border: '0.5px solid #E3E7EB', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', padding: '6px' }}>
                        <button
                          onClick={() => { setImportMenuOpen(false); effectiveIsElite ? openGameImport(null) : setEliteModal('upgrade'); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#141A21', textAlign: 'left' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F6F8')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <Upload style={{ width: '16px', height: '16px', color: '#5A6672', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Import from GameChanger</span>
                          {!effectiveIsElite && <Lock style={{ width: '12px', height: '12px', color: '#9AA5B1', flexShrink: 0 }} />}
                        </button>
                        <button
                          onClick={() => { setImportMenuOpen(false); setIsImportingCalendar(true); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#141A21', textAlign: 'left' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F6F8')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <Calendar style={{ width: '16px', height: '16px', color: '#5A6672', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Import Games from ICS</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Add menu */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => { setAddMenuOpen(o => !o); setImportMenuOpen(false); }}
                      title="Add"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#141A21' }}
                    >
                      <Plus style={{ width: '22px', height: '22px' }} />
                    </button>
                    {addMenuOpen && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '6px', zIndex: 50, minWidth: '200px', backgroundColor: '#FFFFFF', border: '0.5px solid #E3E7EB', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', padding: '6px' }}>
                        <button
                          onClick={() => { setAddMenuOpen(false); setIsAddingGame(true); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#141A21', textAlign: 'left' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F6F8')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <Calendar style={{ width: '16px', height: '16px', color: '#5A6672', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Add Game</span>
                        </button>
                        <button
                          onClick={() => { setAddMenuOpen(false); setIsCreatingTournament(true); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#141A21', textAlign: 'left' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F4F6F8')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <Trophy style={{ width: '16px', height: '16px', color: '#5A6672', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Add Tournament</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 16px 16px' }}>
                {games.length === 0 && seasonTournaments.length === 0 ? (
                  <div style={{ padding: '48px 16px', textAlign: 'center', backgroundColor: '#FFFFFF', boxShadow: cardShadow, borderRadius: '16px' }}>
                    <Calendar style={{ width: '40px', height: '40px', color: '#E3E7EB', margin: '0 auto 12px' }} />
                    <p style={{ fontSize: '13px', fontWeight: 400, color: '#5A6672' }}>No games scheduled.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => setIsImportingCalendar(true)}
                        style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#141A21', color: '#FFFFFF', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                      >
                        Import Calendar
                      </button>
                      <button
                        onClick={() => setIsAddingGame(true)}
                        style={{ fontSize: '12px', fontWeight: 600, color: '#5A6672', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Or Add Manually
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const sorted: any[] = [
                      ...games
                        .filter(g => !g.tournamentId)
                        .map(g => ({ ...g, type: 'game', sortDate: new Date(g.date) })),
                      ...seasonTournaments
                        .filter((t: any) => t.status !== 'completed')
                        .map(t => ({ ...t, type: 'tournament', sortDate: new Date(t.startDate) }))
                    ].sort((a, b) => a.sortDate.getTime() - b.sortDate.getTime());
                    const nextIdx = sorted.findIndex(item => item.sortDate >= today);
                    if (nextIdx > 0) {
                      sorted.splice(nextIdx, 0, { type: 'divider', id: 'next-game-divider', sortDate: today });
                    }
                    return sorted.map((item, idx) => {
                      if (item.type === 'divider') {
                        return (
                          <div key="next-game-divider" ref={nextGameDividerRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '16px 0 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#C9A84C' }} />
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <span style={{ fontSize: '20px', fontWeight: 900, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '-0.02em', lineHeight: 1 }}>Next Game</span>
                              </div>
                              <div style={{ flex: 1, height: '1px', backgroundColor: '#C9A84C' }} />
                            </div>
                          </div>
                        );
                      }
                      if (item.type === 'game') {
                        return (
                          <div key={`game-${item.id}-${idx}`}>
                            <GameCard
                              game={item}
                              isHeadCoach={isHeadCoach}
                              onDelete={deleteGame}
                              onStart={setActiveLineupGame}
                              onResume={setActiveGame}
                              onPrepare={setActivePlanGame}
                              onEdit={setEditingGame}
                              onEditGame={setEditGamePanelGame}
                              onOpenTournament={(id) => {
                                const t = tournaments.find(t => t.id === id);
                                if (t) setActiveTournament(t);
                              }}
                              onImportStats={openGameData}
                              onViewInsights={gameHasInsights(item) ? openGameInsights : undefined}
                              onEnterResults={setEnterResultsGame}
                            />
                          </div>
                        );
                      }
                      return (
                        <div key={`tournament-${item.id}-${idx}`}>
                          <TournamentCard
                            tournament={item}
                            onClick={() => setActiveTournament(item)}
                          />
                        </div>
                      );
                    });
                  })()
                )}


                {/* Post-game upsell — free tier, 2+ completed games */}
                {!effectiveIsElite && isHeadCoach && completedGames.length >= 2 && (
                  <div style={{ backgroundColor: '#FFFFFF', boxShadow: cardShadow, borderRadius: '16px', padding: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#141A21' }}>Your play-by-play is waiting</p>
                    <p style={{ fontSize: '12px', fontWeight: 400, color: '#5A6672', marginTop: '4px', lineHeight: 1.5 }}>
                      You have {completedGames.length} games on the books. Elite coaches get pitch-by-pitch analysis, AI insight cards, and a data-driven batting order, all from a GameChanger export.
                    </p>
                    <button
                      onClick={() => setEliteModal('upgrade')}
                      style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '8px', backgroundColor: '#141A21', color: '#FFFFFF', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      Unlock Elite
                    </button>
                  </div>
                )}

                {/* Practice Mode — persistent entry point for all coaches */}
                {isHeadCoach && (
                  <div style={{ backgroundColor: '#FFFFFF', boxShadow: cardShadow, borderRadius: '16px', padding: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#141A21' }}>Practice the Game Engine</p>
                    <p style={{ fontSize: '12px', fontWeight: 400, color: '#5A6672', marginTop: '2px' }}>Run a demo game with your real roster. Nothing saves.</p>
                    <button
                      onClick={async () => {
                        // Create a temporary practice game doc
                        const practiceGame = await addDoc(collection(db, `teams/${team.id}/games`), {
                          opponent: 'Practice Opponent',
                          date: new Date().toISOString(),
                          status: 'scheduled',
                          isPractice: true,
                          ...(team?.season ? { season: team.season, seasonYear: team.seasonYear } : {}),
                          createdAt: new Date().toISOString(),
                        });
                        // Route practice through the Lineup Builder (same as a real
                        // game) so the coach sets positions before Play Ball —
                        // otherwise they land on an empty diamond with no lineup.
                        // The isPractice flag rides through to the live engine.
                        setActiveLineupGame({ id: practiceGame.id, opponent: 'Practice Opponent', date: new Date().toISOString(), status: 'scheduled', isPractice: true });
                      }}
                      style={{ marginTop: '10px', width: '100%', padding: '10px', borderRadius: '8px', backgroundColor: 'transparent', color: '#141A21', fontSize: '12px', fontWeight: 600, border: '1px solid #141A21', cursor: 'pointer' }}
                    >
                      Start Practice Game →
                    </button>
                  </div>
                )}

                {/* Earlier seasons live at the end of the schedule, below practice.
                    Shown only once a rollover has actually archived something. */}
                {showPastSeasons && <PastSeasonsCard />}
              </div>
    </>
  );
};
